import { useState, type ReactNode } from 'react';
import { Check, Circle, Loader2, Minus } from 'lucide-react';
import type { RecipeStep } from '@workspace/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import type { PlatformTenant } from '../types/platformAdmin.types';

import { SELECT_CLASS } from './recipe.hooks';

/** Piezas compartidas por las recetas del superadmin (Nuevo Cobrador, Nuevo Vendedor). */


export function RecipePage(props: { title: string; intro: string; children: ReactNode }) {
  return (
    <div className='mx-auto w-full max-w-2xl px-4 py-8 sm:py-12'>
      <header className='mb-10'>
        <h1 className='text-3xl font-semibold tracking-tight text-foreground'>{props.title}</h1>
        <p className='mt-2 max-w-prose text-base text-muted-foreground'>{props.intro}</p>
      </header>
      {props.children}
    </div>
  );
}

export function Question(props: {
  done: boolean;
  last?: boolean;
  title: string;
  hint: string;
  children: ReactNode;
}) {
  return (
    <li className={cn('relative pl-10', props.last ? 'pb-0' : 'pb-10')}>
      {props.last ? null : (
        <span
          aria-hidden
          className={cn(
            'absolute left-[11px] top-7 h-[calc(100%-1.75rem)] w-px transition-colors duration-300 motion-reduce:transition-none',
            props.done ? 'bg-primary' : 'bg-border',
          )}
        />
      )}
      <span
        aria-hidden
        className={cn(
          'absolute left-0 top-0.5 flex h-6 w-6 items-center justify-center rounded-full border-2 transition-colors duration-300 motion-reduce:transition-none',
          props.done ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-background',
        )}
      >
        {props.done ? <Check className='h-3.5 w-3.5' strokeWidth={3} /> : null}
      </span>
      <h2 className='text-lg font-semibold leading-7 text-foreground'>
        {props.title}
        <span className='sr-only'>{props.done ? ' (respondida)' : ' (pendiente)'}</span>
      </h2>
      <p className='mb-4 text-sm text-muted-foreground'>{props.hint}</p>
      {props.children}
    </li>
  );
}

/** Cliente, nombre con el que se presenta y nombre del agente (editable con un clic). */
export function ClientFields(props: {
  tenants: PlatformTenant[];
  tenantId: string;
  onTenant: (tenant: PlatformTenant) => void;
  companyName: string;
  onCompanyName: (value: string) => void;
  agentName: string;
  onAgentName: (value: string) => void;
  defaultAgentName: string;
}) {
  const [editing, setEditing] = useState(false);
  const shown = props.agentName.trim() || props.defaultAgentName;
  return (
    <div className='space-y-4'>
      <div className='space-y-2'>
        <Label htmlFor='tenant'>Cliente</Label>
        <select
          id='tenant'
          className={SELECT_CLASS}
          value={props.tenantId}
          onChange={event => {
            const tenant = props.tenants.find(item => item.id === event.target.value);
            if (tenant) props.onTenant(tenant);
          }}
        >
          <option value='' disabled>
            Elegí un cliente
          </option>
          {props.tenants.map(tenant => (
            <option key={tenant.id} value={tenant.id}>
              {tenant.name}
            </option>
          ))}
        </select>
      </div>
      {props.tenantId ? (
        <div className='space-y-2'>
          <Label htmlFor='company'>Nombre con el que se presenta</Label>
          <Input
            id='company'
            value={props.companyName}
            maxLength={120}
            onChange={event => props.onCompanyName(event.target.value)}
          />
          {editing ? (
            <Input
              aria-label='Nombre del agente'
              autoFocus
              value={props.agentName}
              placeholder={props.defaultAgentName}
              maxLength={120}
              onChange={event => props.onAgentName(event.target.value)}
              onBlur={() => setEditing(false)}
            />
          ) : (
            <p className='text-sm text-muted-foreground'>
              El agente se va a llamar <span className='font-medium text-foreground'>{shown}</span>.{' '}
              <button
                type='button'
                className='font-medium text-primary underline-offset-4 hover:underline focus-visible:underline focus-visible:outline-none'
                onClick={() => setEditing(true)}
              >
                Cambiar
              </button>
            </p>
          )}
        </div>
      ) : null}
    </div>
  );
}

export function RecipeFooter(props: {
  error: string;
  progress: string;
  answered: boolean[];
  submitLabel: string;
}) {
  const done = props.answered.filter(Boolean).length;
  const total = props.answered.length;
  const ready = done === total;
  const busy = Boolean(props.progress);
  return (
    <div className='mt-10 space-y-3 border-t border-border pt-6'>
      {props.error ? (
        <p role='alert' className='text-sm font-medium text-destructive'>
          {props.error}
        </p>
      ) : null}
      <div className='flex flex-col-reverse items-stretch gap-3 sm:flex-row sm:items-center sm:justify-between'>
        <p className='text-sm text-muted-foreground' aria-live='polite'>
          {busy ? props.progress : ready ? 'Todo listo.' : `Faltan ${total - done} de ${total} respuestas.`}
        </p>
        <Button type='submit' size='lg' disabled={!ready || busy}>
          {busy ? <Loader2 className='mr-2 h-4 w-4 animate-spin' aria-hidden /> : null}
          {props.submitLabel}
        </Button>
      </div>
    </div>
  );
}

export function RecipeDone(props: {
  title: string;
  subtitle: string;
  steps: RecipeStep[];
  aiReady: boolean;
  note?: ReactNode;
  actions: ReactNode;
}) {
  return (
    <div className='mx-auto w-full max-w-2xl px-4 py-8 sm:py-12'>
      <header className='mb-8'>
        <h1 className='text-3xl font-semibold tracking-tight text-foreground'>{props.title}</h1>
        <p className='mt-2 text-base text-muted-foreground'>{props.subtitle}</p>
      </header>

      <ul className='divide-y divide-border border-y border-border'>
        {props.steps.map(step => (
          <StepRow key={step.id} step={step} />
        ))}
      </ul>

      {props.note ? <p className='mt-4 text-sm text-muted-foreground'>{props.note}</p> : null}

      {props.aiReady ? null : (
        <p role='alert' className='mt-4 rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive'>
          El cliente no tiene clave de OpenAI. Cargala en su ficha de Tenants antes de activar el agente.
        </p>
      )}

      <div className='mt-8 flex flex-col gap-3 sm:flex-row'>{props.actions}</div>
    </div>
  );
}

function StepRow({ step }: { step: RecipeStep }) {
  const icon =
    step.status === 'done' ? (
      <Check className='h-4 w-4 text-success' strokeWidth={3} aria-hidden />
    ) : step.status === 'skipped' ? (
      <Minus className='h-4 w-4 text-muted-foreground' aria-hidden />
    ) : (
      <Circle className='h-4 w-4 text-primary' aria-hidden />
    );
  return (
    <li className='flex items-start gap-3 py-3'>
      <span className='mt-0.5'>{icon}</span>
      <span className='min-w-0'>
        <span className={cn('block text-sm font-medium', step.status === 'pending' ? 'text-primary' : 'text-foreground')}>
          {step.label}
        </span>
        {step.detail ? <span className='block text-sm text-muted-foreground'>{step.detail}</span> : null}
      </span>
    </li>
  );
}
